import CancelScreenContent from "@components/Booking/Cancel/index";
import {useCurrentPatientQuery} from "@components/Booking/graphql/__generated__/current-patient.type";
import MaybeQuit from "@components/Booking/Quit/MaybeQuit";
import SexAtBirthBottomSheet from "@components/Booking/SexAtBirthBottomSheet";
import Popup from "@components/Popup/Popup";
import {BookingStep} from "@page-containers/booking/utils/bookingStep";
import {clearEmail} from "@page-containers/booking/utils/emailStorage";
import useBookingStep from "@page-containers/booking/utils/useBookingStep";
import {useRouter} from "next/router";
import {useTranslation} from "@i18n/client";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useDisclosure} from "src/hooks/useDisclosure";
import {v4} from "uuid";

import {useMonolithLocationFieldsBySlugQuery} from "@services/graphql/queries/location";
import {CarbonPage} from "../../CarbonPage";
import {Logo, Nav} from "../../components/composition/Nav";
import Button from "../../components/v5/Button";
import Icon from "../../components/v5/Icon";
import {isSaasLocation} from "../../constants/locations";
import {I18nNamespace} from "@i18n";
import {ApptDetails} from "../../utils/analytics/eventQueue";
import {useGeolocation} from "../../utils/browser-storage/location";
import {removeFalsyKeys} from "../../utils/removeFalsyKeys";
import {useFetchAppointmentReasonsQuery} from "./graphql/__generated__/FetchAppointmentReasons.type";
import {buildBookingLink, parseBookingQuery, useParsedBookingQuery} from "./utils/bookingQuery";

import {roundedMilesBetweenCoordinates} from "src/utils/distance";
import {useLocationBySlugQuery} from "@components/Booking/LocationSelection/__generated__/bookable-location.type";

const Booking: CarbonPage = () => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const router = useRouter();
  const sexAtBirthBottomSheet = useDisclosure();

  const {
    originUrl,
    appointmentReasonSlug,
    canceled,
    canceling,
    quitting,
    selectedSlotIds,
    selectedTime,
    locationSlug,
    specialtyId,
    practiceId,
    showBackButton,
  } = useParsedBookingQuery();
  const patientQuery = useCurrentPatientQuery();

  /*
    We are moving away from this monolith query to the graphql Bookable Location model.
    At the time of writing, these fields are still needed from monolith (may not be precise).
      "googlePlaceId"
      "hours"
      "locationSpecialties"
      "region"
      "specialtyIds"
  */
  const monolithLocationFieldsQuery = useMonolithLocationFieldsBySlugQuery(locationSlug);

  const locationQuery = useLocationBySlugQuery({
    variables: {
      practiceId,
      slug: locationSlug || "",
    },
    skip: !locationSlug,
  });

  const graphqlLocation = locationQuery.data?.practice?.location;

  const monolithLocationFields = monolithLocationFieldsQuery?.data?.location || null;

  const {data: apptReasonData, loading: apptReasonLoading} = useFetchAppointmentReasonsQuery({
    variables: {
      slugs: appointmentReasonSlug === null ? [] : [appointmentReasonSlug],
    },
    skip: !appointmentReasonSlug,
  });
  const practiceAppointmentReason = apptReasonData?.practice?.appointmentReasons?.nodes?.[0];

  const closeUrl = originUrl || `/get-care/${appointmentReasonSlug}`;
  const [bookingStep, element] = useBookingStep(
    sexAtBirthBottomSheet.open,
    monolithLocationFields,
    apptReasonLoading,
    monolithLocationFieldsQuery.loading || locationQuery.loading,
    practiceAppointmentReason,
  );

  const [firstStep, setFirstStep] = useState<BookingStep>();

  useEffect(() => {
    setFirstStep(oldFirstStep => {
      if (oldFirstStep) {
        return oldFirstStep;
      }
      if (bookingStep !== BookingStep.LOADING) {
        return bookingStep;
      }
      return undefined;
    });
  }, [bookingStep]);

  const isFirstStep = useMemo(
    () => firstStep === undefined || firstStep === bookingStep,
    [bookingStep, firstStep],
  );

  const isLastStep = bookingStep === BookingStep.APPOINTMENT_CONFIRMATION;
  const isFirstOrLastStep = isFirstStep || isLastStep;

  const userPosition = useGeolocation();

  const apptDetails: ApptDetails = useMemo(() => {
    const milesAway =
      userPosition &&
      graphqlLocation?.locationAddress?.latitude &&
      graphqlLocation?.locationAddress?.longitude
        ? roundedMilesBetweenCoordinates(
            userPosition.x,
            userPosition.y,
            graphqlLocation?.locationAddress?.latitude,
            graphqlLocation?.locationAddress?.longitude,
          )
        : null;
    return removeFalsyKeys({
      apptReasonId: practiceAppointmentReason?.appointmentReason?.id,
      specialtyId,
      locationId: graphqlLocation?.id,
      slotId: selectedSlotIds?.[0],
      milesAway,
      timeToAppt: selectedTime ? selectedTime - Date.now() : selectedTime,
    });
  }, [
    graphqlLocation?.id,
    graphqlLocation?.locationAddress?.latitude,
    graphqlLocation?.locationAddress?.longitude,
    practiceAppointmentReason?.appointmentReason?.id,
    selectedSlotIds,
    selectedTime,
    specialtyId,
    userPosition,
  ]);

  const openQuitScreen = useCallback(() => {
    router.push(
      buildBookingLink({
        ...parseBookingQuery(router.query),
        quitting: true,
      }),
      undefined,
      {shallow: true},
    );
  }, [router]);

  const elementOrQuit =
    quitting && graphqlLocation && !patientQuery.loading ? (
      <MaybeQuit
        location={graphqlLocation}
        time={selectedTime}
        originUrl={closeUrl}
        apptDetails={apptDetails}
        bookingStep={bookingStep}
      />
    ) : (
      element
    );

  const onBack = useCallback(() => {
    if (bookingStep === BookingStep.ACCOUNT_CREATION) {
      clearEmail();
    } else {
      router.back();
    }
  }, [bookingStep, router]);

  const onCancelReconsider = useCallback(() => {
    if (canceled) {
      router.push(closeUrl);
    } else {
      router.back();
    }
  }, [canceled, closeUrl, router]);

  const ariaDescriptionId = useMemo(v4, []);
  const isSaas = useMemo(
    () => (monolithLocationFields ? isSaasLocation(monolithLocationFields) : false),
    [monolithLocationFields],
  );

  return (
    <>
      <Nav hide={quitting}>
        <Button
          variant="ghost"
          size="sm"
          className={showBackButton || !isFirstOrLastStep ? "" : "invisible"}
          onClick={onBack}
        >
          <Icon icon="dropdown-arrow-down" className="fs14 mr2 rotate-90 hidden sm:inline-block" />
          {t("Back", {ns: "web-scheduling"})}
        </Button>
        <div className={isSaas || monolithLocationFieldsQuery.loading ? "opacity-0" : ""}>
          <Logo />
        </div>
        <Button
          variant="ghost"
          size="sm"
          className={isFirstStep ? "hidden" : ""}
          href={isLastStep ? closeUrl : null}
          onClick={isLastStep ? undefined : openQuitScreen}
        >
          {isLastStep ? t("Done", {ns: "web-scheduling"}) : t("Cancel", {ns: "web-scheduling"})}
        </Button>

        <Button variant="ghost" size="sm" className={isFirstStep ? "" : "hidden"} href={closeUrl}>
          {t("Close", {ns: "web-scheduling"})}
        </Button>
      </Nav>
      <main className="bg-white px-4 py-8 sm:pt-[76px] min-h-[calc(100vh-72px)]">
        <div className="max-w-[40.5rem] m-auto">{elementOrQuit}</div>
      </main>
      <footer />
      <Popup
        aria-label={t("Canceling", {ns: "web-scheduling"})}
        aria-describedby={ariaDescriptionId}
        open={!!canceling || !!canceled}
        onClose={onCancelReconsider}
      >
        <CancelScreenContent
          ariaDescriptionId={ariaDescriptionId}
          location={monolithLocationFields}
          selectedTime={selectedTime}
        />
      </Popup>
      <SexAtBirthBottomSheet
        close={sexAtBirthBottomSheet.close}
        isOpen={sexAtBirthBottomSheet.isOpen}
      />
    </>
  );
};

export default Booking;
